import {
  Checkbox,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import styles from "../../pages/read-documents/styles/tableResults.module.scss";
import { Box } from "@mui/system";
import { ITable } from "./interfaces/table.interface";
import { RouterGuard } from "./RouteGuard";
import { Spinner } from "./Spinner";
import {
  debounceFunction,
  saveSearchHistoryAutorizationPayments,
  saveSearchHistoryElectronicDocument,
  selectIndex,
} from "shared/utils";
import { useDispatch } from "react-redux";
import { Dispatch } from "shared/store/store";
import { useTableResults } from "shared/hooks/useTableResults";
import { IParamsUrl } from "shared/models";
import { FooterTable } from "./table/components/FooterTable";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

export const TableComponent = (props: ITable<any>) => {
  const dispatch = useDispatch<Dispatch>();
  const {
    columns,
    valuesResultsPages,
    rowSelected,
    selectAllRows,
    selectRow,
    isSearch = true,
    rowHeader = true,
  } = props;
  const {
    electronicDocuments,
    isLoading,
    sizeRows,
    searcher,
    checked,
    handleChangePage,
    handleChangeSizePage,
    handleSortByColumn,
    activityIsDefeated,
    currentPath,
  } = useTableResults();
  const { content, contentSize, totalElements, size, number, paramsUrl, url } =
    electronicDocuments;
  const { open, text } = searcher;
  const params: IParamsUrl = {
    ...paramsUrl,
    filter: text,
  };
  const [hasSearch, setHasSearch] = useState(isSearch);

  // Función para determinar si una columna está siendo ordenada
  const isColumnSorted = (columnSortable: string) => {
    if (!paramsUrl?.orderBy) return false;
    const [sortedColumn] = Number(paramsUrl.orderBy).toString().split(":");
    return sortedColumn === columnSortable;
  };

  // Función para obtener la dirección del ordenamiento
  const getSortDirection = () => {
    if (!paramsUrl?.orderBy) return null;
    const [, direction] = Number(paramsUrl.orderBy).toString().split(":");
    return direction;
  };

  // Función para renderizar el icono de ordenamiento
  const renderSortIcon = (columnSortable: string) => {
    const isSorted = isColumnSorted(columnSortable);
    const direction = getSortDirection();

    if (!isSorted || !direction) {
      return <KeyboardArrowDownIcon className={styles.sortIcon} />;
    }

    if (direction === "asc") {
      return <KeyboardArrowUpIcon className={styles.sortIcon} />;
    }

    return <KeyboardArrowDownIcon className={styles.sortIcon} />;
  };

  useEffect(() => {
    if (open && hasSearch) {
      debounceFunction(() => {
        if (params && url) {
          dispatch.electronicDocuments.getElectronicDocuments(params, url);
        }
        if (url && selectIndex(currentPath[0]) === 1) {
          saveSearchHistoryElectronicDocument({ params, url });
        }
        if (url && selectIndex(currentPath[0]) === 3) {
          saveSearchHistoryAutorizationPayments({ params, url });
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  useEffect(() => {
    content && !hasSearch && setHasSearch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  useEffect(() => {
    return () => {
      dispatch.collapseState.clearCollapse();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <React.Fragment>
      {totalElements > 0 ? (
        <Paper
          className={styles.containerTableResults}
          style={{ display: "flex", flexDirection: "column" }}
        >
          <TableContainer className={styles.tableResults}>
            <Table
              sx={{ minWidth: 700 }}
              aria-label="customized table"
              data-testid="table-results"
            >
              {columns.length > 0 && (
                <>
                  <TableHead>
                    <TableRow className={styles.tablaHeader}>
                      {rowSelected && selectAllRows && (
                        <TableCell
                          padding="checkbox"
                          className={styles.tableCell}
                        >
                          {rowHeader && (
                            <Checkbox
                              color="primary"
                              indeterminate={
                                rowSelected.length > 0 &&
                                rowSelected.length < contentSize
                              }
                              checked={
                                contentSize > 0 &&
                                rowSelected.length === contentSize
                              }
                              onChange={(event) =>
                                selectAllRows(event, content)
                              }
                              inputProps={{
                                "aria-label": "select all docs",
                              }}
                              data-testid="checkbox-select-all"
                              id={"selectAll"}
                            />
                          )}
                        </TableCell>
                      )}
                      {columns.map((column, index) => (
                        <React.Fragment key={column.field + "_" + index}>
                          {column?.rowGuard ? (
                            <TableCell
                              align="left"
                              className={styles.tableCell}
                              style={{
                                minWidth: column.minWidth,
                                display: column.isHidden ? "none" : "",
                              }}
                              data-testid={`table-header-${column.field}`}
                            >
                              <RouterGuard
                                roles={column.rowGuard.roles}
                                type="component"
                              >
                                <>
                                  {column.headerName}
                                  {column.sortable && (
                                    <IconButton
                                      onClick={() =>
                                        handleSortByColumn(column.sortable)
                                      }
                                      style={{ padding: "0" }}
                                      className={`${styles.sortButton} ${
                                        isColumnSorted(column.sortable)
                                          ? styles.sortButtonActive
                                          : ""
                                      }`}
                                      data-testid={`sort-button-${column.field}`}
                                    >
                                      {renderSortIcon(column.sortable)}
                                    </IconButton>
                                  )}
                                </>
                              </RouterGuard>
                            </TableCell>
                          ) : (
                            <TableCell
                              align="left"
                              className={styles.tableCell}
                              style={{
                                minWidth: column.minWidth,
                                display: column.isHidden ? "none" : "",
                              }}
                            >
                              {column.headerName}
                              {column.sortable && (
                                <IconButton
                                  onClick={() =>
                                    handleSortByColumn(column.sortable)
                                  }
                                  style={{ padding: "0" }}
                                  className={`${styles.sortButton} ${
                                    isColumnSorted(column.sortable)
                                      ? styles.sortButtonActive
                                      : ""
                                  }`}
                                  data-testid={`sort-button-${column.field}`}
                                >
                                  {renderSortIcon(column.sortable)}
                                </IconButton>
                              )}
                            </TableCell>
                          )}
                        </React.Fragment>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody data-testid="table-results-body">
                    {content!.map((row: any, index) => (
                      <TableRow key={index}>
                        {selectRow && rowSelected && (
                          <TableCell
                            padding="checkbox"
                            style={{
                              padding: "0",
                            }}
                          >
                            <Checkbox
                              color="primary"
                              inputProps={{
                                "aria-labelledby": `check${index}`,
                              }}
                              checked={rowSelected.indexOf(row.id) !== -1}
                              onChange={(e) => selectRow(e, row.id, row)}
                              className={styles.checkboxFiles}
                              id={`check${index}`}
                            />
                          </TableCell>
                        )}
                        {columns.map((column, index2) => (
                          <React.Fragment key={column.field + "_" + index2}>
                            {column.rowGuard ? (
                              <TableCell
                                align="left"
                                className={`${styles.tableCell} ${
                                  column.isReduceCellPadding && styles.padding1
                                }`}
                                style={{
                                  display: column.isHidden ? "none" : "",
                                }}
                                data-testid={`table-cell-${column.field}`}
                              >
                                <RouterGuard
                                  roles={column.rowGuard.roles}
                                  type="component"
                                >
                                  {column.renderCell
                                    ? column.renderCell(row)
                                    : row}
                                </RouterGuard>
                              </TableCell>
                            ) : (
                              <TableCell
                                align="left"
                                className={`${styles.tableCell} ${
                                  column.isReduceCellPadding && styles.padding1
                                }`}
                                key={column.field + index2}
                                style={{
                                  display: column.isHidden ? "none" : "",
                                }}
                                data-testid={`table-cell2-${column.field}`}
                              >
                                {column.renderCell
                                  ? column.renderCell(row)
                                  : row}
                              </TableCell>
                            )}
                          </React.Fragment>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </>
              )}
            </Table>
          </TableContainer>
          {valuesResultsPages && (
            <FooterTable
              handleChangePage={handleChangePage}
              handleChangeSizePage={handleChangeSizePage}
              numberPage={number}
              size={size}
              sizeRows={sizeRows}
              totalElements={totalElements}
              valuesResultsPages={valuesResultsPages}
              contentSize={contentSize}
            />
          )}
        </Paper>
      ) : (
        <Box
          className={styles.containerResults}
          data-testid="message_no_search"
        >
          No se encontraron resultados relacionados a su búsqueda
        </Box>
      )}
      {isLoading && <Spinner />}
    </React.Fragment>
  );
};
